<template>
  <Loading v-if="isLoading" />
  <div v-else>
    <vs-prompt
      title="Create new operation link"
      @accept="onAcceptCreateNew"
      :active.sync="isCreateNewPromptActive"
      :is-valid="validForm"
      accept-text="Add"
    >
      <div class="flex flex-col w-full max-w-md">
        <vs-input label="Title" v-model="title" class="w-full" />
        <vs-input label="Description" v-model="description" class="w-full" />
        <vs-input label="Operation link url" v-model="url" class="w-full" />
        <vs-input label="Icon url" v-model="icon" class="w-full" />
      </div>
    </vs-prompt>

    <div class="w-full flex mb-3 justify-end">
      <vs-button @click="isCreateNewPromptActive = true">Create new</vs-button>
    </div>

    <vx-card class="mb-5">
      <div
        :href="link.url"
        target="_blank"
        v-for="(link, index) in sortedOperationLinks"
        :key="index"
        class="text-black block hover:pl-5"
        :class="{ 'pointer-events-none': link.linkType != 'Any' }"
      >
        <li
          class="p-4 mb-3 flex justify-between items-center border border-light-grey rounded-lg cursor-pointer"
          :class="{ 'opacity-50': link.linkType != 'Any' }"
          style="background-color: #f8fafb"
          v-if="!editingOperationLink || editingOperationLink.id != link.id"
        >
          <div class="flex items-center">
            <div style="width: 50px" class="flex justify-center">
              <div v-if="link.icon">
                <img :src="link.icon" alt="Link icon" style="width: 50px; max-width: 50px; max-height: 50px" />
              </div>
              <feather-icon
                v-else
                icon="LinkIcon"
                svgClasses="h-4 w-4"
                class="p-2 inline-flex rounded-md bg-white border border-light-grey shadow"
              ></feather-icon>
            </div>
            <div>
              <p class="ml-4 text-gray-700 font-semibold font-sans tracking-wide">
                {{ link.title }}
              </p>
              <p class="ml-4 text-gray-600 font-sans tracking-wide">
                {{ link.description }}
              </p>
              <p class="ml-4 text-grey font-sans tracking-wide max-w-md truncate">
                {{ link.url }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <div v-for="country in link.countries" :key="country">
              <img v-if="country" width="24" class="opacity-75" :src="countryToFlag(country)" />
              <div v-else>Unknown country</div>
            </div>
            <!-- <vs-button v-if="editingOperationLink == null" @click="onClickEdit(link)">Edit</vs-button> -->
            <feather-icon
              icon="EditIcon"
              svgClasses="w-5 h-5 hover:text-danger stroke-current"
              class="ml-4"
              @click.stop="onClickEdit(link)"
            />
            <feather-icon
              icon="TrashIcon"
              svgClasses="w-5 h-5 hover:text-danger stroke-current"
              class="ml-4"
              @click.stop="onClickRemove(link)"
            />
          </div>
        </li>
        <!-- If editing -->
        <li
          v-else
          class="p-4 mb-3 flex justify-between items-center border border-light-grey rounded-lg cursor-pointer"
          style="background-color: #f8fafb"
        >
          <div class="w-full flex items-center gap-4">
            <div v-if="link.icon">
              <img :src="link.icon" alt="Link icon" style="width: 50px; max-width: 50px; max-height: 50px" />
            </div>
            <div class="flex flex-col w-full max-w-md">
              <vs-input label="Title" v-model="editingOperationLink.title" class="w-full" />
              <vs-input label="Description" v-model="editingOperationLink.description" class="w-full" />
              <vs-input label="Operation link url" v-model="editingOperationLink.url" class="w-full" />
              <vs-input label="Icon url" v-model="editingOperationLink.icon" class="w-full" />
            </div>
          </div>
          <div>
            <vs-button @click="onClickSave">Save</vs-button>
          </div>
        </li>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FlagDK from "@/assets/images/flags/dan.png";
import FlagNO from "@/assets/images/flags/nor.png";
import FlagFI from "@/assets/images/flags/fin.png";
import FlagUK from "@/assets/images/flags/eng.png";

import _ from "lodash";

export default {
  components: {},
  data() {
    return {
      FlagDK,
      FlagNO,
      FlagFI,
      FlagUK,

      editingOperationLink: null,

      isCreateNewPromptActive: false,
      title: "",
      description: "",
      url: "",
      icon: ""
    };
  },
  methods: {
    countryToFlag(country) {
      switch (country) {
        case "DK":
          return FlagDK;
        case "NO":
          return FlagNO;
        case "FI":
          return FlagFI;
        case "UK":
          return FlagUK;
        default:
          return null;
      }
    },
    onClickEdit(link) {
      // Cannot start editing another link if already editing
      if (this.editingOperationLink != null) {
        return;
      }

      this.editingOperationLink = link;
    },
    onClickRemove(link) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Are you sure you want to delete this operation link?",
        text: "This action is irreversible.",
        accept: async () => {
          try {
            await this.$store.dispatch("operationLinks/delete", link.id);
            await this.$store.dispatch("operationLinks/getAllByCountry", this.activeCountry.id);
            this.$vs.notify({
              title: "Success",
              text: "Operation link removed",
              color: "green"
            });
          } catch (e) {
            this.$vs.notify({
              title: "Error",
              text: "Could not remove operation link",
              color: "danger"
            });
          }
        }
      });
    },
    async onClickSave() {
      try {
        await this.$store.dispatch("operationLinks/update", {
          id: this.editingOperationLink.id,
          dto: {
            title: this.editingOperationLink.title,
            description: this.editingOperationLink.description,
            url: this.editingOperationLink.url,
            icon: this.editingOperationLink.icon
          }
        });
        this.$vs.notify({
          title: "Success",
          text: "Operation link updated",
          color: "green"
        });

        this.editingOperationLink = null;
      } catch (e) {
        this.$vs.notify({
          title: "Error",
          text: "Could not update operation link",
          color: "danger"
        });
      }
    },

    async onAcceptCreateNew() {
      try {
        await this.$store.dispatch("operationLinks/create", {
          country: this.activeCountry.id,
          title: this.title,
          description: this.description,
          url: this.url,
          icon: this.icon
        });
        await this.$store.dispatch("operationLinks/getAllByCountry", this.activeCountry.id);
        this.$vs.notify({
          title: "Success",
          text: "Operation link created",
          color: "green"
        });

        this.title = "";
        this.description = "";
        this.url = "";
        this.icon = "";
        this.isCreateNewPromptActive = false;
      } catch (err) {
        this.$vs.notify({
          title: "Error",
          text: "Could not create operation link",
          color: "danger"
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      operationLinks: "operationLinks/operationLinksByCountry",
      isLoading: "operationLinks/loading",
      activeCountry: "venues/activeCountry"
    }),
    sortedOperationLinks() {
      // All non-any operation links in the bottom
      return _.sortBy(this.operationLinks, e => e.deviceType != "Any");
    },
    validForm() {
      return this.title != "" && this.url != "";
    }
  },
  created() {},
  async mounted() {
    await this.$store.dispatch("operationLinks/getAllByCountry", this.activeCountry.id);
    console.log("Got operation links by country", this.operationLinks);
  }
};
</script>

<style lang="scss">
</style>